import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  ArrowLeft, TrendingUp, Users, Heart, MessageCircle, 
  Eye, DollarSign, Share2, Loader2, BarChart3, Calendar,
  ChevronDown, Radio, Swords, Gift as GiftIcon, Clock, Trophy
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
  { value: 'all', label: 'All Time' }
];

export default function AnalyticsDashboardPage() {
  const { token, language } = useAuth();
  const tr = t(language);
  const navigate = useNavigate();
  
  const [period, setPeriod] = useState('30d');
  const [analytics, setAnalytics] = useState(null);
  const [liveAnalytics, setLiveAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPeriodMenu, setShowPeriodMenu] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const [main, live] = await Promise.all([
        axios.get(`${API}/creator/analytics?period=${period}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/analytics/live?period=${period}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null })),
      ]);
      setAnalytics(main.data);
      setLiveAnalytics(live.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [token, period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color = 'primary', trend }) => (
    <Card className="relative overflow-hidden">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground mb-1">{title}</p>
            <p className="text-3xl font-bold">{value}</p>
            {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className={`w-12 h-12 rounded-2xl bg-${color}/10 flex items-center justify-center`}>
            <Icon className={`w-6 h-6 text-${color}`} />
          </div>
        </div>
        {trend !== undefined && (
          <div className={`mt-3 flex items-center gap-1 text-sm ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`}>
            <TrendingUp className={`w-4 h-4 ${trend < 0 ? 'rotate-180' : ''}`} />
            <span>{Math.abs(trend)}% {trend >= 0 ? 'increase' : 'decrease'}</span>
          </div>
        )}
      </CardContent>
      <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-${color} to-${color}/50`} />
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const overview = analytics?.overview || {};
  const followers = analytics?.followers || {};
  const revenue = analytics?.revenue || {};

  return (
    <div className="max-w-6xl mx-auto pb-20" data-testid="analytics-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-primary" />
              <h1 className="font-bold text-lg">Analytics Dashboard</h1>
            </div>
          </div>
          
          {/* Period Selector */}
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={() => setShowPeriodMenu(!showPeriodMenu)}
              data-testid="period-selector"
            >
              <Calendar className="w-4 h-4 mr-2" />
              {PERIODS.find(p => p.value === period)?.label}
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
            
            {showPeriodMenu && (
              <div className="absolute right-0 top-full mt-2 bg-background border border-border rounded-xl shadow-lg py-1 min-w-[140px] z-50">
                {PERIODS.map((p) => (
                  <button
                    key={p.value}
                    onClick={() => {
                      setPeriod(p.value);
                      setShowPeriodMenu(false);
                    }}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-secondary transition-colors ${
                      period === p.value ? 'bg-secondary font-semibold' : ''
                    }`}
                    data-testid={`period-${p.value}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="p-4 space-y-6">
        {/* Overview Stats */}
        <section>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Overview
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard 
              icon={Eye} 
              title="Views" 
              value={formatNumber(overview.views_in_period)} 
              subtitle={`${formatNumber(overview.estimated_views)} total`}
              color="blue-500"
            />
            <StatCard 
              icon={Heart} 
              title="Likes" 
              value={formatNumber(overview.likes_in_period)} 
              subtitle={`${formatNumber(overview.total_likes)} total`}
              color="pink-500"
            />
            <StatCard 
              icon={MessageCircle} 
              title="Comments" 
              value={formatNumber(overview.comments_in_period)} 
              subtitle={`${formatNumber(overview.total_comments)} total`}
              color="violet-500"
            />
            <StatCard 
              icon={Share2} 
              title="Shares" 
              value={formatNumber(overview.total_shares)} 
              color="green-500"
            />
          </div>
        </section>

        {/* Engagement Rate */}
        <Card className="bg-gradient-to-br from-primary/10 to-violet-500/10">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Engagement Rate</p>
                <p className="text-4xl font-bold text-primary">{overview.engagement_rate || 0}%</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Based on likes and comments per follower
                </p>
              </div>
              <div className="w-20 h-20 rounded-full border-4 border-primary/30 flex items-center justify-center">
                <TrendingUp className="w-8 h-8 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Followers Section */}
        <section>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Followers
          </h2>
          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6 text-center">
                <Users className="w-8 h-8 text-primary mx-auto mb-2" />
                <p className="text-3xl font-bold">{formatNumber(followers.total)}</p>
                <p className="text-sm text-muted-foreground">Total Followers</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <TrendingUp className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-3xl font-bold text-green-500">+{formatNumber(followers.new_in_period)}</p>
                <p className="text-sm text-muted-foreground">New Followers</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <Users className="w-8 h-8 text-violet-500 mx-auto mb-2" />
                <p className="text-3xl font-bold">{formatNumber(followers.following)}</p>
                <p className="text-sm text-muted-foreground">Following</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Revenue Section */}
        <section>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-primary" />
            Revenue
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-2 border-green-500/30">
              <CardContent className="pt-6">
                <DollarSign className="w-8 h-8 text-green-500 mb-2" />
                <p className="text-3xl font-bold text-green-500">
                  ${revenue.total_earnings?.toFixed(2) || '0.00'}
                </p>
                <p className="text-sm text-muted-foreground">Total Earnings</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground mb-1">Tips</p>
                <p className="text-2xl font-bold">
                  ${revenue.period_breakdown?.tips?.amount?.toFixed(2) || '0.00'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {revenue.period_breakdown?.tips?.count || 0} tips received
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground mb-1">Subscriptions</p>
                <p className="text-2xl font-bold">
                  ${revenue.period_breakdown?.subscriptions?.monthly_revenue?.toFixed(2) || '0.00'}/mo
                </p>
                <p className="text-xs text-muted-foreground">
                  {revenue.period_breakdown?.subscriptions?.active_subscribers || 0} active
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground mb-1">Sales</p>
                <p className="text-2xl font-bold">
                  ${revenue.period_breakdown?.sales?.revenue?.toFixed(2) || '0.00'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {revenue.period_breakdown?.sales?.count || 0} orders
                </p>
              </CardContent>
            </Card>
          </div>
          
          {/* Pending Balance */}
          {revenue.pending_balance > 0 && (
            <Card className="mt-4 bg-yellow-500/10 border-yellow-500/30">
              <CardContent className="pt-6 flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-600">Pending Balance</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    ${revenue.pending_balance?.toFixed(2)}
                  </p>
                </div>
                <Button 
                  onClick={() => navigate('/withdrawals')}
                  className="rounded-full bg-yellow-500 hover:bg-yellow-600"
                >
                  Withdraw
                </Button>
              </CardContent>
            </Card>
          )}
        </section>

        {/* Top Posts */}
        {analytics?.top_posts?.length > 0 && (
          <section>
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Top Performing Posts
            </h2>
            <div className="space-y-3">
              {analytics.top_posts.map((post, index) => (
                <Card 
                  key={post.id} 
                  className="cursor-pointer hover:bg-secondary/50 transition-colors"
                  onClick={() => navigate(`/post/${post.id}`)}
                  data-testid={`top-post-${index}`}
                >
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-violet-500 flex items-center justify-center text-white font-bold">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">
                          {post.content || 'Media post'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(post.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Heart className="w-4 h-4 text-pink-500" />
                          {formatNumber(post.likes_count)}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageCircle className="w-4 h-4 text-blue-500" />
                          {formatNumber(post.comments_count)}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* === LIVE Analytics === */}
        {liveAnalytics && (liveAnalytics.overview?.total_streams > 0 || liveAnalytics.battles?.total > 0) && (
          <section data-testid="live-analytics-section">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Radio className="w-5 h-5 text-red-500" />
              Live Streaming
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <Card data-testid="live-streams-card">
                <CardContent className="pt-6 text-center">
                  <Radio className="w-7 h-7 text-red-500 mx-auto mb-2" />
                  <p className="text-3xl font-bold">{liveAnalytics.overview.total_streams || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Streams</p>
                </CardContent>
              </Card>
              <Card data-testid="live-time-card">
                <CardContent className="pt-6 text-center">
                  <Clock className="w-7 h-7 text-blue-500 mx-auto mb-2" />
                  <p className="text-3xl font-bold">
                    {Math.floor((liveAnalytics.overview.total_live_seconds || 0) / 60)}m
                  </p>
                  <p className="text-xs text-muted-foreground">Total Live Time</p>
                </CardContent>
              </Card>
              <Card data-testid="peak-viewers-card">
                <CardContent className="pt-6 text-center">
                  <Eye className="w-7 h-7 text-violet-500 mx-auto mb-2" />
                  <p className="text-3xl font-bold">{formatNumber(liveAnalytics.overview.peak_viewers || 0)}</p>
                  <p className="text-xs text-muted-foreground">Peak Viewers</p>
                </CardContent>
              </Card>
              <Card data-testid="gift-coins-card">
                <CardContent className="pt-6 text-center">
                  <GiftIcon className="w-7 h-7 text-amber-500 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-amber-500">
                    {formatNumber(liveAnalytics.gifts?.total_coins || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">Gift Coins Earned</p>
                </CardContent>
              </Card>
            </div>

            {/* Battles */}
            {liveAnalytics.battles?.total > 0 && (
              <Card className="bg-gradient-to-br from-pink-500/10 to-purple-500/10" data-testid="battles-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Swords className="w-5 h-5 text-pink-500" />
                    Battles
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold">{liveAnalytics.battles.total}</p>
                      <p className="text-xs text-muted-foreground">Total</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-emerald-500 flex items-center justify-center gap-1">
                        <Trophy className="w-5 h-5" />
                        {liveAnalytics.battles.wins}
                      </p>
                      <p className="text-xs text-muted-foreground">Wins</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-500">{liveAnalytics.battles.losses}</p>
                      <p className="text-xs text-muted-foreground">Losses</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-violet-500">{liveAnalytics.battles.win_rate}%</p>
                      <p className="text-xs text-muted-foreground">Win Rate</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent streams */}
            {liveAnalytics.recent_streams?.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="recent-streams-list">
                <h3 className="text-sm font-semibold text-muted-foreground">Recent Streams</h3>
                {liveAnalytics.recent_streams.slice(0, 5).map((s) => (
                  <Card key={s.id}>
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold truncate">{s.title || 'Untitled stream'}</p>
                          <p className="text-xs text-muted-foreground">
                            {s.started_at ? new Date(s.started_at).toLocaleDateString() : ''}
                            {' · '}
                            {Math.floor(s.duration_seconds / 60)} min
                          </p>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground flex-shrink-0">
                          <span className="flex items-center gap-1"><Eye className="w-4 h-4" />{formatNumber(s.peak_viewers)}</span>
                          <span className="flex items-center gap-1"><Heart className="w-4 h-4 text-pink-500" />{formatNumber(s.likes_count)}</span>
                          <span className="flex items-center gap-1 text-amber-500"><GiftIcon className="w-4 h-4" />{formatNumber(s.total_gifts)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Posts Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Content Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-secondary/50 rounded-xl text-center">
                <p className="text-3xl font-bold">{overview.total_posts || 0}</p>
                <p className="text-sm text-muted-foreground">Total Posts</p>
              </div>
              <div className="p-4 bg-secondary/50 rounded-xl text-center">
                <p className="text-3xl font-bold text-green-500">+{overview.posts_in_period || 0}</p>
                <p className="text-sm text-muted-foreground">Posts This Period</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
